"use client";

import { motion } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardBody } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { AuthorAvatars, type AuthorChip, toAuthorChips } from "@/components/widget/author-avatars";
import { HelpListRow } from "@/components/widget/help-list-row";
import { StickySearchHeader } from "@/components/widget/sticky-search-header";
import { getMockHelpArticles, getMockHelpCategories } from "@/dummy";
import {
  useVisitorHelpFoldersQuery,
  useVisitorHelpListQuery,
} from "@/lib/graphql/queries/generated/help.generated";
import { isDemoReady, useDemoContent } from "@/lib/use-demo-translator";
import { useFocusOnMount } from "@/lib/use-focus-on-mount";

const PAGE_SIZE = 10;
const LOAD_MORE_THRESHOLD = 240;
/** How many distinct author avatars stack in the folder header.
 *  Anything past this rolls into the "and N more" copy. */
const HEADER_AUTHOR_LIMIT = 3;

interface HelpCategoryViewProps {
  /** Folder id (legacy prop name — folders replaced categories on BE,
   *  the visitor-surface concept is the same). */
  categoryId: string;
  /** Folder display metadata threaded down from the route so the
   *  header renders without a per-folder BE fetch. Optional — when
   *  the visitor deep-links into a category the header silently
   *  degrades to just the list. */
  categoryName?: string;
  categoryDescription?: string;
  categoryArticleCount?: number;
  isDummy: boolean;
  /** Drill into a subfolder — the shell re-routes to a new
   *  `help-category` view targeting that folder. */
  onOpenCategory: (
    categoryId: string,
    meta?: { name?: string; description?: string; articleCount?: number },
  ) => void;
  onOpenArticle: (slug: string) => void;
}

/**
 * Folder drill-in view (help center). Sticky search at
 * the top, a header card (folder name, description,
 * article count, author byline + avatar stack), then a divider-
 * separated list of subfolders + articles. The visitor can keep
 * drilling: tapping a subfolder routes to another `help-category`
 * view; tapping an article opens the detail.
 *
 * `recursive: false` so articles + subfolders stay in sync — if the
 * recursive flag were on, articles deep in the subtree would render
 * alongside subfolder tiles and the hierarchy collapses visually.
 *
 * Search uses the same `visitorHelpList` consolidated query (just
 * pass `query`); search is global, jumps the visitor out of the
 * folder context until they clear the input.
 *
 * Pagination: `visitorHelpList` returns `{ items, meta }`; we
 * fetchMore with `offset` on bottom-scroll. Same pattern as News.
 */
export function HelpCategoryView({
  categoryId,
  categoryName,
  categoryDescription,
  categoryArticleCount,
  isDummy,
  onOpenCategory,
  onOpenArticle,
}: HelpCategoryViewProps) {
  const t = useTranslations("help");
  const tDemo = useDemoContent();
  const focusRef = useFocusOnMount<HTMLDivElement>();
  const locale = useLocale();
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query, 300);
  const isSearching = debounced.length >= 2;

  // ─── Subfolders (direct children) ─────────────────────────────
  const { data: subfolderData } = useVisitorHelpFoldersQuery({
    variables: { parentId: categoryId, locale },
    skip: isDummy,
  });
  const subfolders = useMemo(() => subfolderData?.visitorHelpFolders ?? [], [subfolderData]);

  // ─── Articles (this folder only, paginated) ───────────────────
  const {
    data: articlesData,
    loading: articlesLoading,
    fetchMore,
  } = useVisitorHelpListQuery({
    variables: {
      args: {
        folderId: categoryId,
        locale,
        recursive: false,
        limit: PAGE_SIZE,
        offset: 0,
      },
    },
    skip: isDummy,
    notifyOnNetworkStatusChange: true,
  });

  // ─── Search (consolidated list query with `query` arg) ────────
  const { data: searchData, loading: searchLoading } = useVisitorHelpListQuery({
    variables: { args: { query: debounced, locale, limit: 10 } },
    skip: debounced.length < 2 || isDummy,
  });

  const mockCategory = isDummy
    ? getMockHelpCategories(tDemo).find((c) => c.id === categoryId)
    : null;
  const mockArticles = isDummy
    ? getMockHelpArticles(tDemo).filter((a) => a.categoryId === categoryId)
    : [];

  const articles: ArticleRowData[] = isDummy
    ? mockArticles.map((a) => ({
        id: a.id,
        slug: a.slug,
        title: a.title,
        excerpt: a.excerpt as string | null,
        authors: toAuthorChips((a as { authors?: unknown }).authors),
        reactionCounts: null,
      }))
    : (articlesData?.visitorHelpList?.items ?? []).map((a) => ({
        id: a._id,
        slug: a.slug,
        title: a.title,
        excerpt: a.excerpt ?? null,
        authors: toAuthorChips(a.authors),
        reactionCounts: (a.reactionCounts as Record<string, number> | null | undefined) ?? null,
      }));

  const dummySearchResults: ArticleRowData[] = isDummy
    ? getMockHelpArticles(tDemo)
        .filter(
          (a) =>
            a.title.toLowerCase().includes(debounced.toLowerCase()) ||
            a.excerpt.toLowerCase().includes(debounced.toLowerCase()),
        )
        .map((a) => ({
          id: a.id,
          slug: a.slug,
          title: a.title,
          excerpt: a.excerpt as string | null,
          authors: toAuthorChips((a as { authors?: unknown }).authors),
          reactionCounts: null,
        }))
    : [];
  const realSearchResults: ArticleRowData[] = (searchData?.visitorHelpList?.items ?? []).map(
    (r) => ({
      id: r._id,
      slug: r.slug,
      title: r.title,
      excerpt: r.excerpt ?? null,
      authors: toAuthorChips(r.authors),
      reactionCounts: (r.reactionCounts as Record<string, number> | null | undefined) ?? null,
    }),
  );
  const searchResults = isDummy ? dummySearchResults : realSearchResults;

  const list = isSearching ? searchResults : articles;

  // ─── Resolve header metadata — route prop > demo mock > nothing.
  const headerTitle = categoryName ?? mockCategory?.title ?? "";
  const headerDescription = categoryDescription ?? mockCategory?.description ?? "";
  const headerArticleCount = categoryArticleCount ?? mockCategory?.articleCount ?? articles.length;
  // Author byline = dedupe authors across the articles in this
  // folder. Mirrors the familiar help-center header where you see
  // who's behind the collection. Limited to the first few unique chips.
  const headerAuthors = useMemo(() => {
    const seen = new Map<string, AuthorChip>();
    for (const a of articles) {
      for (const author of a.authors ?? []) {
        if (!seen.has(author.id)) seen.set(author.id, author);
      }
    }
    return Array.from(seen.values());
  }, [articles]);
  const displayedAuthors = headerAuthors.slice(0, HEADER_AUTHOR_LIMIT);
  const remainingAuthors = Math.max(0, headerAuthors.length - displayedAuthors.length);
  const firstAuthorName = displayedAuthors[0]?.name ?? "";

  // ─── Load-more ────────────────────────────────────────────────
  const hasMore =
    !isSearching && !isDummy && (articlesData?.visitorHelpList?.meta?.hasNextPage ?? false);

  const [loadingMore, setLoadingMore] = useState(false);
  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          args: {
            folderId: categoryId,
            locale,
            recursive: false,
            limit: PAGE_SIZE,
            offset: articlesData?.visitorHelpList?.items?.length ?? 0,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            ...prev,
            visitorHelpList: {
              ...fetchMoreResult.visitorHelpList,
              items: [
                ...(prev.visitorHelpList?.items ?? []),
                ...(fetchMoreResult.visitorHelpList?.items ?? []),
              ],
            },
          };
        },
      });
    } catch {
      // The next page failed (offline / 5xx): keep what is already listed. `loadingMore` resets
      // below, so scrolling to the bottom again retries.
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, categoryId, locale, fetchMore, articlesData]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const remaining = el.scrollHeight - el.clientHeight - el.scrollTop;
      if (remaining < LOAD_MORE_THRESHOLD) void loadMore();
    },
    [loadMore],
  );

  // Demo copy comes from a lazily loaded bundle: skeleton until it lands, not an "empty" card.
  const demoPending = isDummy && !isDemoReady(tDemo);
  const firstLoad =
    demoPending ||
    (!isDummy &&
      !isSearching &&
      articlesLoading &&
      articles.length === 0 &&
      subfolders.length === 0);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ScrollArea
        followDirection
        className="min-h-0 flex-1"
        viewportRef={focusRef}
        onScroll={handleScroll}
      >
        <StickySearchHeader
          value={query}
          onChange={setQuery}
          placeholder={t("searchPlaceholder")}
        />

        {!isSearching && headerTitle && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="border-wx-border border-b px-5 py-5"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <h2 className="font-semibold text-lg text-wx-fg">{headerTitle}</h2>
                {headerDescription && (
                  <p className="text-sm text-wx-fg-muted leading-snug">{headerDescription}</p>
                )}
                <div className="mt-1 flex flex-col gap-0.5 text-wx-fg-subtle text-xs">
                  <span>{t("articlesCount", { count: headerArticleCount })}</span>
                  {firstAuthorName && (
                    <span>
                      {t("authorsByline", {
                        first: firstAuthorName,
                        remaining: remainingAuthors,
                      })}
                    </span>
                  )}
                </div>
              </div>
              {displayedAuthors.length > 0 && (
                <div className="shrink-0">
                  <AuthorAvatars authors={displayedAuthors} />
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Articles FIRST, subfolders SECOND, all in ONE container so
            the `border-b last:border-b-0` rule on `HelpListRow` only
            suppresses the very last row of the combined list — keeps
            the divider between the last article and the first
            subfolder. Splitting into two containers made the rule fire
            at the end of EACH list and the seam between sections
            dropped. */}
        {firstLoad ? (
          <ListSkeleton />
        ) : list.length === 0 && subfolders.length === 0 ? (
          <div className="px-5 py-6">
            <Card>
              <CardBody className="py-8 text-center">
                <p className="text-sm text-wx-fg-muted">
                  {isSearching ? t("noResults") : t("emptyTitle")}
                </p>
              </CardBody>
            </Card>
          </div>
        ) : (
          <div className="flex flex-col">
            {list.map((a, idx) => (
              <HelpListRow
                key={a.id}
                title={a.title}
                description={a.excerpt}
                authors={a.authors}
                kind="article"
                onClick={() => onOpenArticle(a.slug)}
                delay={Math.min(idx, PAGE_SIZE) * 0.03}
              />
            ))}
            {!isSearching &&
              subfolders.map((f, idx) => (
                <HelpListRow
                  key={f._id}
                  title={f.name}
                  description={f.description}
                  meta={
                    (f.articleCount ?? 0) > 0
                      ? t("articlesCount", { count: f.articleCount ?? 0 })
                      : undefined
                  }
                  kind="folder"
                  onClick={() =>
                    onOpenCategory(f._id, {
                      name: f.name,
                      description: f.description ?? undefined,
                      articleCount: f.articleCount ?? 0,
                    })
                  }
                  delay={(list.length + idx) * 0.03}
                />
              ))}
            {(loadingMore || searchLoading) && (
              <div className="flex justify-center py-3">
                <Skeleton className="h-3 w-24" />
              </div>
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

interface ArticleRowData {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  authors?: AuthorChip[];
  reactionCounts?: Record<string, number> | null;
}

function ListSkeleton() {
  return (
    <div className="flex flex-col">
      {Array.from({ length: 3 }).map((_, idx) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder list
          key={idx}
          className="flex items-center gap-3 border-wx-border border-b px-5 py-4 last:border-b-0"
        >
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-32 rounded" />
            <Skeleton className="h-3 w-56 rounded" />
          </div>
          <Skeleton className="h-4 w-4 rounded" />
        </div>
      ))}
    </div>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}
